import { memo, useCallback } from "react";
import { NaceIndustryDto } from "@advanced-sales-technologies/backend-client/models/NaceIndustryDto";
import clsx from "clsx";

import Checkbox from "@/components/atoms/Checkbox";
import InputLabel from "@/components/atoms/input/InputLabel";

export type ProcessedIndustryData = {
  descendantIds: Record<string, string[]>;
  titleMap: Map<string, string>;
  mainIdMap: Map<string, string | null>;
};

const NACEIndustryRenderOption = memo<{
  option: NaceIndustryDto;
  selectedIds: Set<string>;
  industryData: ProcessedIndustryData;
  onSelectionChange: (ids: string[], add: boolean) => void;
}>(({ option, selectedIds, industryData, onSelectionChange }) => {
  const { descendantIds } = industryData;
  const optionDescendants = descendantIds[option.id] || [];
  const totalDesc = optionDescendants.length;
  const selectedDescCount = optionDescendants.filter((id) => selectedIds.has(id)).length;

  const hasChildren = totalDesc > 0;
  const isChecked = hasChildren ? selectedDescCount === totalDesc : selectedIds.has(option.id);
  const isIndeterminate = hasChildren && selectedDescCount > 0 && selectedDescCount < totalDesc;

  const handleToggle = useCallback(() => {
    if (hasChildren) {
      onSelectionChange([option.id, ...optionDescendants], !isChecked);
    } else {
      onSelectionChange([option.id], !isChecked);
    }
  }, [option.id, hasChildren, isChecked, optionDescendants, onSelectionChange]);

  const label = (
    [`${option.section}${option.division || ""}`, option.group, option.clazz].filter((opt) => opt !== null).join(".") +
    ": " +
    option.title
  ).trim();

  return (
    <>
      <div className={clsx("flex select-none items-center gap-1.5 pb-2 pr-2 disabled-within:cursor-not-allowed")}>
        <Checkbox
          className="mt-0.5"
          id={option.id}
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={handleToggle}
          sizing="sm"
        />
        <InputLabel label={label} htmlFor={option.id} className={"w-full cursor-pointer"} />
      </div>

      {option.children.length > 0 && (
        <div className="pl-4">
          {option.children.map((child) => (
            <NACEIndustryRenderOption
              key={child.id}
              option={child}
              selectedIds={selectedIds}
              industryData={industryData}
              onSelectionChange={onSelectionChange}
            />
          ))}
        </div>
      )}
    </>
  );
});

export default NACEIndustryRenderOption;
